/**
 * @fileoverview IncidentReport Component - Main orchestrator for incident report creation and editing
 * Handles the flow between different states: creation options, AI generation, and editing.
 * 
 * @author SmarterServices Platform Team
 * @version 1.0.0
 * @since 2025-10-21
 */

import React, { useState } from 'react';
import { useExamSessionStore, getStore } from '../../../stores/StoreContext';
import CreateOptions from './CreateOptions';
import GenerateOptions from './GenerateOptions';
import ReportEditor from './ReportEditor';
import SubmissionSuccess from './SubmissionSuccess';

/**
 * IncidentReport Component
 * 
 * Main orchestrator that manages the incident report workflow:
 * - Shows creation options for new reports (scratch vs AI)
 * - Handles AI generation flow with annotation selection
 * - Displays the report editor for editing and management
 * - Manages state transitions between different views
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onClose - Optional callback to close the modal/container
 * @returns {JSX.Element} The appropriate view based on current state
 */
function IncidentReport({ onClose }) {
  const incidentReport = useExamSessionStore((state) => state.incidentReport);
  const ExamSessionStore = getStore('examSessionStore');
  const AppStore = getStore('appStore');

  // Local state for managing the creation flow
  const [currentView, setCurrentView] = useState('default'); // 'default', 'create-options', 'generate', 'editor', 'success'
  const [generatedData, setGeneratedData] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  // Determine what to show based on incident report state and current view
  const getViewToRender = () => {
    // If just submitted, show success view
    if (justSubmitted) {
      return 'success';
    }

    // If there's an existing incident report, show the editor
    if (incidentReport) {
      return 'editor';
    }

    // For new reports, follow the creation flow
    switch (currentView) {
      case 'create-options':
        return 'create-options';
      case 'generate':
        return 'generate';
      case 'editor':
        return 'editor';
      case 'success':
        return 'success';
      default:
        return 'create-options'; // Start with creation options for new reports
    }
  };

  // Handle creating from scratch
  const handleCreateFromScratch = () => {
    setCurrentView('editor');
    setGeneratedData(null);
  };

  // Handle AI generation choice
  const handleGenerateWithAI = () => {
    setCurrentView('generate');
  };

  // Handle AI generation completion
  const handleAIGenerated = (data) => {
    setGeneratedData(data);
    setCurrentView('editor');
  };

  // Handle canceling from generation or editor (back to options)
  const handleCancel = () => {
    setCurrentView('create-options');
    setGeneratedData(null);
  };

  // Handle successful report submission
  const handleReportSubmitted = () => {
    setJustSubmitted(true);
  };

  // Handle marking session as reviewed
  const handleMarkAsReviewed = async () => {
    try {
      await ExamSessionStore.setSessionAsReviewed({ reviewByUser: AppStore.user.username });

      // Close the incident report window after marking as reviewed
      handleCloseIncidentReport();
    } catch (error) {
      console.error('Error marking session as reviewed:', error);
    }
  };

  // Handle returning to view the report
  const handleViewReport = () => {
    setJustSubmitted(false);
    // This will show the editor view since incidentReport exists
  };

  // Handle closing the incident report window
  const handleCloseIncidentReport = () => {
    // Reset all state and close the modal/window
    setCurrentView('default');
    setGeneratedData(null);
    setJustSubmitted(false);
    // Close the draggable modal if this is in a modal
    onClose?.();
  };

  // Render the appropriate view
  const viewToRender = getViewToRender();

  switch (viewToRender) {
    case 'create-options':
      return (
        <CreateOptions
          onCreateFromScratch={handleCreateFromScratch}
          onGenerateWithAI={handleGenerateWithAI}
        />
      );

    case 'generate':
      return (
        <GenerateOptions
          onGenerate={handleAIGenerated}
        />
      );

    case 'editor':
      return (
        <ReportEditor
          initialData={generatedData || {}}
          onCancel={!incidentReport ? handleCancel : undefined} // Only show cancel for new reports
          onSubmitted={handleReportSubmitted} // Handle successful submission
        />
      );

    case 'success':
      return (
        <SubmissionSuccess
          onMarkAsReviewed={handleMarkAsReviewed}
          onViewReport={handleViewReport}
        />
      );

    default:
      return (
        <CreateOptions
          onCreateFromScratch={handleCreateFromScratch}
          onGenerateWithAI={handleGenerateWithAI}
        />
      );
  }
}

export default IncidentReport;
